import { ANP_OUTCOME_NAMES, animalPartyBetLabel, animalPartyGroupOf } from '../animalparty.js';

export function betTypeLabel(betType: string): string {
  // Every game prefixes its wire codes, so every game needs stripping here or an
  // operator reads "BAC BANKER PAIR" in a transaction row. Prefixes are listed
  // rather than stripped generically (e.g. /^[A-Z]+_/) because a generic rule
  // would also eat the leading word of an unprefixed code.
  // Crash encodes the player's TARGET in the bet type — `CRASH_250` is a bet on
  // 2.50x — because the place-bet body carries no free payload. Stripping the
  // prefix generically would leave an operator reading "250", so it is decoded
  // back into the multiplier it means.
  const crashTarget = /^CRASH_([1-9][0-9]*)$/.exec(betType);
  if (crashTarget) return `${(Number(crashTarget[1]) / 100).toFixed(2)}x`;

  // A Wingo code is a colour or one number. Stripping the prefix alone would
  // leave an operator reading "NUM 7", so both kinds are spelled out the way
  // the game's own payout table names them. `trace-format.test.ts` holds these
  // to the game package's English labels.
  const wingoNumber = /^WG_NUM_([0-9])$/.exec(betType);
  if (wingoNumber) return `Number ${wingoNumber[1]}`;
  const wingoColour = /^WG_(GREEN|PURPLE|RED)$/.exec(betType);
  if (wingoColour) return WINGO_COLOUR_NAMES[wingoColour[1] as WingoColour];

  // A Fish Prawn Crab code names a family and one or two symbols. Stripping the
  // prefix alone would leave an operator reading "LONG FISH CRAB", so all
  // twenty-seven are spelled out the way the game's own payout table names
  // them. `trace-format.test.ts` holds these to the game package's English
  // labels. A Long Card names its two symbols in board order, so a code in the
  // other order, or one naming a symbol twice, is not one of the table's bets.
  const fpcOne = /^FPC_(SINGLE|PAIR)_([A-Z]+)$/.exec(betType);
  if (fpcOne && isFpcSymbol(fpcOne[2])) {
    return `${FPC_FAMILY_NAMES[fpcOne[1] as 'SINGLE' | 'PAIR']}: ${FPC_SYMBOL_NAMES[fpcOne[2]]}`;
  }
  const fpcLong = /^FPC_LONG_([A-Z]+)_([A-Z]+)$/.exec(betType);
  if (
    fpcLong &&
    isFpcSymbol(fpcLong[1]) &&
    isFpcSymbol(fpcLong[2]) &&
    FPC_SYMBOLS.indexOf(fpcLong[1]) < FPC_SYMBOLS.indexOf(fpcLong[2])
  ) {
    return `${FPC_FAMILY_NAMES.LONG}: ${FPC_SYMBOL_NAMES[fpcLong[1]]} + ${FPC_SYMBOL_NAMES[fpcLong[2]]}`;
  }
  // Mines Chest carries the picked chests inside the bet type:
  // `MINESCHEST_3_7_12` is one bet on chests 3, 7 and 12. It reads the way the
  // game's own history prints it. Only a legal ticket is decoded (one to ten
  // chest numbers from 1 to 20, ascending, no leading zero); anything else is
  // left to the generic rule below rather than given chests it never named.
  const chests = minesChestTicket(betType);
  if (chests) return `Chests ${chests.join(' · ')}`;
  // Space Hero carries the player's target inside the bet type, as hundredths:
  // `SPACEHERO_250` is a bet that the flight reaches 2.50x. Only a legal target
  // (1.01x to 10000.00x, no leading zero) is decoded; anything else is left to
  // the generic rule below rather than given a multiplier it never meant.
  const spaceheroTarget = /^SPACEHERO_([1-9][0-9]{0,6})$/.exec(betType);
  if (spaceheroTarget) {
    const hundredths = Number(spaceheroTarget[1]);
    if (hundredths >= 101 && hundredths <= 1_000_000) {
      return `Target ${(hundredths / 100).toFixed(2)}x`;
    }
  }
  // Animal Party's eleven spots take the game package's own English names
  // (mirrored in ../animalparty.ts and pinned against the package by a test),
  // so a row reads "Land Animals" rather than a bare "LAND". An ANP_ code that
  // is not one of the eleven falls through and is stripped like any other.
  const animalPartySpot = animalPartyBetLabel(betType);
  if (animalPartySpot) return animalPartySpot;

  return betType
    .replace(/^SICBO_/, '')
    .replace(/^DT_/, '')
    .replace(/^BAC_/, '')
    .replace(/^SDR_/, '')
    .replace(/^LDR_/, '')
    .replace(/^WG_/, '')
    .replace(/^FPC_/, '')
    .replace(/^ANP_/, '')
    .replace(/_/g, ' ');
}

type WingoColour = 'GREEN' | 'PURPLE' | 'RED';

const WINGO_COLOUR_NAMES: Record<WingoColour, string> = {
  GREEN: 'Green',
  PURPLE: 'Purple',
  RED: 'Red',
};

/**
 * Fish Prawn Crab's six die symbols in BOARD order, and their English names.
 * Mirrors FPC_SYMBOLS and SYMBOL_LABELS.en in the game package, which this
 * bundle cannot import (its entry point reaches the seeded draw, and so
 * `node:crypto`). `trace-format.test.ts` holds both to the package.
 */
export const FPC_SYMBOLS = ['FISH', 'CRAB', 'CHICKEN', 'GOURD', 'PRAWN', 'COIN'] as const;
type FpcSymbol = (typeof FPC_SYMBOLS)[number];

const FPC_SYMBOL_NAMES: Record<FpcSymbol, string> = {
  FISH: 'Fish',
  CRAB: 'Crab',
  CHICKEN: 'Chicken',
  GOURD: 'Gourd',
  PRAWN: 'Prawn',
  COIN: 'Coin',
};

const FPC_FAMILY_NAMES = { SINGLE: 'Three Soldiers', PAIR: 'Pair', LONG: 'Long Card' } as const;

function isFpcSymbol(value: unknown): value is FpcSymbol {
  return typeof value === 'string' && (FPC_SYMBOLS as readonly string[]).includes(value);
}

/** Three Fish Prawn Crab symbols, as a round stores its dice. */
function isFpcDice(value: unknown): value is [FpcSymbol, FpcSymbol, FpcSymbol] {
  return Array.isArray(value) && value.length === 3 && value.every(isFpcSymbol);
}

/**
 * Whether a result of unknown origin is a Fish Prawn Crab round: three symbol
 * dice with the per-symbol `counts` the game classifies them into. The other
 * dice tables store their faces as numbers, so a symbol triple is this game's
 * alone. For callers that are not told the game, like the rounds explorer.
 */
export function isFishPrawnCrabResult(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const r = value as { dice?: unknown; counts?: unknown };
  return (
    isFpcDice(r.dice) &&
    typeof r.counts === 'object' &&
    r.counts !== null &&
    !Array.isArray(r.counts)
  );
}

/** The chests a Mines Chest bet type names, or null when it is not a legal ticket. */
function minesChestTicket(betType: string): number[] | null {
  const m = /^MINESCHEST_([0-9]+(?:_[0-9]+){0,9})$/.exec(betType);
  if (!m) return null;
  const chests: number[] = [];
  for (const part of m[1]!.split('_')) {
    if (!/^[1-9][0-9]?$/.test(part)) return null;
    const chest = Number(part);
    if (chest > 20 || chest <= (chests[chests.length - 1] ?? 0)) return null;
    chests.push(chest);
  }
  return chests;
}

export function formatResult(gameId: string, resultData: unknown): string {
  const r = resultData as Record<string, unknown> | null;
  if (!r || typeof r !== 'object') return String(resultData ?? '—');
  if (gameId === 'sicbo' && Array.isArray((r as { dice?: unknown }).dice)) {
    const dice = (r as { dice: number[] }).dice;
    return `🎲 ${dice.join('·')} = ${(r as { total?: number }).total ?? dice.reduce((a, b) => a + b, 0)}`;
  }
  if (gameId === 'dragontiger' && typeof (r as { winner?: unknown }).winner === 'string') {
    const crit = (r as { critMultiplier?: number }).critMultiplier ?? 1;
    return `${(r as { winner: string }).winner}${crit > 1 ? ` ×${crit}` : ''}`;
  }
  if (gameId === 'greedyking' && typeof (r as { primary?: unknown }).primary === 'string') {
    const special = (r as { special?: string | null }).special;
    return `${(r as { primary: string }).primary}${special ? ` + ${special}` : ''}`;
  }
  if (gameId === 'baccarat' && typeof (r as { winner?: unknown }).winner === 'string') {
    // Player/Banker totals are what an operator is reconciling against, and the
    // pair flags decide whether a side bet paid — so both belong in the row.
    // Without this branch the cell printed a truncated JSON blob.
    const w = (r as { winner: string }).winner;
    const pt = (r as { playerTotal?: number }).playerTotal ?? '?';
    const bt = (r as { bankerTotal?: number }).bankerTotal ?? '?';
    const flags: string[] = [];
    if ((r as { natural?: boolean }).natural) flags.push('natural');
    if ((r as { playerPair?: boolean }).playerPair) flags.push('P pair');
    if ((r as { bankerPair?: boolean }).bankerPair) flags.push('B pair');
    return `${w} · P ${pt} / B ${bt}${flags.length ? ` · ${flags.join(', ')}` : ''}`;
  }
  if (
    (gameId === 'shortdicerush' || gameId === 'longdicerush') &&
    Array.isArray((r as { dice?: unknown }).dice)
  ) {
    // Both tables classify a roll by total and parity, and never void on a
    // triple — so the row shows what an operator is actually reconciling
    // against: the faces, the total, and the two tags that decided the board.
    const dice = (r as { dice: number[] }).dice;
    const total = (r as { total?: number }).total ?? dice.reduce((a, b) => a + b, 0);
    const size = (r as { isBig?: boolean }).isBig ? 'BIG' : 'SMALL';
    const parity = (r as { isOdd?: boolean }).isOdd ? 'ODD' : 'EVEN';
    const triple = (r as { isTriple?: boolean }).isTriple ? ' · triple' : '';
    return `🎲 ${dice.join('·')} = ${total} · ${size} ${parity}${triple}`;
  }
  if (gameId === 'wingo' && typeof (r as { number?: unknown }).number === 'number') {
    // One number per round, and its colours are what settled every colour bet,
    // so the row shows both. The base colour comes first and Purple second, the
    // way a two-colour number is drawn: 0 is Red + Purple, 5 is Green + Purple.
    const colours: string[] = [];
    if ((r as { green?: boolean }).green) colours.push(WINGO_COLOUR_NAMES.GREEN);
    if ((r as { red?: boolean }).red) colours.push(WINGO_COLOUR_NAMES.RED);
    if ((r as { purple?: boolean }).purple) colours.push(WINGO_COLOUR_NAMES.PURPLE);
    const n = (r as { number: number }).number;
    return colours.length ? `${n} · ${colours.join(' + ')}` : String(n);
  }
  if (gameId === 'fishprawncrab' && isFpcDice((r as { dice?: unknown }).dice)) {
    // Three symbol dice, in the order they were drawn. Every bet on the table
    // settles on which symbols show and how often, which the three names say
    // at a glance, so the per-symbol counts are not repeated.
    return (r as { dice: FpcSymbol[] }).dice.map((s) => FPC_SYMBOL_NAMES[s]).join(' · ');
  }
  if (gameId === 'crash' && typeof (r as { crashBps?: unknown }).crashBps === 'number') {
    // One number per round, and the two boundaries an operator reconciles
    // against are worth naming rather than leaving them to be read off the
    // decimals: 1.00x paid nobody, 10000.00x is the ceiling.
    const bps = (r as { crashBps: number }).crashBps;
    const x = `${(bps / 10_000).toFixed(2)}x`;
    if (bps <= 10_000) return `${x} · bust`;
    if (bps >= 100_000_000) return `${x} · max`;
    return x;
  }
  if (gameId === 'mineschest' && isChestList((r as { explosive?: unknown }).explosive)) {
    // The round's whole result is which ten chests were explosive. The other
    // ten held treasure, so listing one side is the full layout, and it is the
    // side a losing bet is reconciled against.
    return `Explosive: ${(r as { explosive: number[] }).explosive.join(' ')}`;
  }
  if (gameId === 'spacehero' && typeof (r as { finalBps?: unknown }).finalBps === 'number') {
    // One multiplier per round, stored in bps. The two ends of the range are
    // named because they are what an operator reconciles against: at 1.00x the
    // launch failed and every bet lost, and 10000.00x is the ceiling.
    const bps = (r as { finalBps: number }).finalBps;
    const x = `${(bps / 10_000).toFixed(2)}x`;
    if (bps <= 10_000) return `${x} · failed launch`;
    if (bps >= 100_000_000) return `${x} · max`;
    return x;
  }
  if (gameId === 'animalparty' && typeof (r as { outcome?: unknown }).outcome === 'string') {
    // One result per round: an animal on the ring, or the King in the centre.
    // A King round pays nobody, so its row says so: an operator reconciling a
    // row with no payouts can see why at a glance.
    const outcome = (r as { outcome: string }).outcome;
    const name = ANP_OUTCOME_NAMES[outcome] ?? outcome;
    if (outcome === 'king') return `👑 ${name} · every bet loses`;
    const group = animalPartyGroupOf(outcome);
    return group ? `${name} · ${group}` : name;
  }
  if (gameId === 'singaporelottery' && Array.isArray((r as { digits?: unknown }).digits)) {
    const digits = (r as { digits: number[] }).digits;
    const sum = (r as { sum?: number }).sum ?? digits.reduce((a, b) => a + b, 0);
    return `${digits.join('·')} · sum ${sum}`;
  }
  // Unknown game: show something rather than nothing, but never the whole blob.
  // singaporelottery's result carries ~20 derived boolean flags, and dumping it
  // raw stretched the history row far past the viewport and forced the entire
  // page to scroll sideways. Truncating keeps an unmapped game readable and
  // keeps the table's geometry intact.
  const json = JSON.stringify(r);
  return json.length > 80 ? `${json.slice(0, 80)}…` : json;
}

/** A non-empty list of whole numbers: the shape of a Mines Chest layout. */
function isChestList(value: unknown): value is number[] {
  return Array.isArray(value) && value.length > 0 && value.every((c) => Number.isInteger(c));
}
